/**
 * Spec 053, step 2: notes of experience of tasks closed without looking it through
 * («Опыт не разобран»). People decide them in «Знания»: into a new method, into a new version of
 * one, or turned down with a reason. Step 4: the weekly duty over the methods' base. The routes
 * are the app's only — no agent tool calls them.
 */

import { api } from "@/api/client";

export interface SimilarMethod {
  id: string;
  title: string;
  status: string | null;
  score: number;
}

export interface ExperienceNote {
  id: string;
  task_id: string | null;
  order_id: string | null;
  author: string;
  body_md: string;
  state: "open" | "merged" | "rejected";
  similar: SimilarMethod[];
  created_at: string;
}

export interface ToMethodBody {
  note_ids: string[];
  body_md: string;
  method_id?: string | null;
  title?: string | null;
  summary?: string | null;
  change_note?: string | null;
}

/** Step 4: the weekly duty over the methods' base, as people read it. */
export interface DutyMethod {
  id: string;
  title: string;
  status: string;
  area_id: string | null;
  created_at: string | null;
}

export interface DutyReport {
  week_start: string;
  created_at: string;
  pairs: { first: DutyMethod; second: DutyMethod; distance: number }[];
  stale_drafts: DutyMethod[];
  stuck_notes: { unit_id: string | null; unit_name: string | null; count: number; oldest: string }[];
  routine: { agent_id: string; agent_name: string; unit_id: string; unit_name: string; count: number }[];
}

const NOTES = "/api/v1/experience-notes";

export const Experience = {
  list: () => api<ExperienceNote[]>(NOTES),
  duty: () => api<DutyReport | null>("/api/v1/method-duty"),
  toMethod: (body: ToMethodBody) =>
    api<{ method_id: string; method_title: string; version_number: number; new_method: boolean }>(`${NOTES}/methods`, {
      method: "POST",
      body,
      idempotent: true,
    }),
  reject: (id: string, reason_md: string) =>
    api<ExperienceNote>(`${NOTES}/${id}/reject`, { method: "POST", body: { reason_md }, idempotent: true }),
};

/** «3 заметки» — the count in a line of «Знания» and «Решить». */
export function notesLine(n: number): string {
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? "заметка" : d >= 2 && d <= 4 && (h < 12 || h > 14) ? "заметки" : "заметок";
  return `${n} ${word}`;
}

/** «похожих пар: 2 · застряли заметки: 3» — what a week's duty found, for the tile in «Знания». */
export function dutyLine(report: DutyReport | null | undefined): string {
  if (!report) return "раз в неделю, по понедельникам";
  const parts = [
    report.pairs.length && `похожих пар: ${report.pairs.length}`,
    report.stale_drafts.length && `черновиков без применений: ${report.stale_drafts.length}`,
    report.stuck_notes.length && `застряли заметки: ${report.stuck_notes.reduce((n, s) => n + s.count, 0)}`,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "в базе порядок";
}
